import { designTokens } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import Link from "next/link";
import type { ComponentProps } from "react";
import { useState } from "react";
import { InvoiceItemAddForm } from "~/components/common/constructions/adding/InvoiceItemAddForm";
import { ChangeAmountModal } from "~/components/common/constructions/change_amount/ChangeAmountModal";
import { ConstructionList } from "~/components/common/constructions/list/ConstructionList";
import { ConstructionTop } from "~/components/common/constructions/top/ConstructionTop";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { ClientDealLayout } from "~/components/common/layouts/deals/ClientDealLayout";
import Loading from "~/components/common/parts/loading/Loading";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { SettlementList } from "~/components/common/settlements/list/SettlementList";
import { useDealBillings } from "~/hooks/deals/useDealBillings";
import { useDealConstructionItems } from "~/hooks/useDealConstructionItems";
import { useDeals } from "~/hooks/useDeals";
import useItandiAccount from "~/hooks/useItandiAccount";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { NskResponse } from "~/models/Api";
import type {
	DealConstruction,
	DealConstructionEdit
} from "~/models/DealConstruction";
import type {
	DealSettlement,
	DealSettlementEdit
} from "~/models/DealSettlement";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { PATH } from "~/pages/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import {
	destroy as destroyBilling,
	create as postDealBilling,
	update as updateDealBilling,
	updateSeq
} from "~/utils/api/deals/DealBilling";
import { getDownloadPath } from "~/utils/api/deals/DealBillingFile";
import { create as generateBillings } from "~/utils/api/deals/DealBillingGenerate";
import { getDownloadPath as getSettlementDownloadPath } from "~/utils/api/deals/DealSettlementFile";
import Styles from "./Page.module.scss";

export function Page() {
	const { notify } = useNotification();
	const [showChangeAmountModal, setShowChangeAmountModal] = useState(false);
	const { match: isSp } = useMediaQuery(MEDIA_QUERY.sp);
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const { data: deal } = useDeals().show(id);
	const { data: dealConstructionItems } = useDealConstructionItems();
	const { data: currentAccount } = useItandiAccount().currentAccount();
	const { data: billingContext, mutate: mutateBillings } =
		useDealBillings().index(id, "rentee");
	if (
		deal === null ||
		billingContext === null ||
		dealConstructionItems === null ||
		currentAccount === null
	) {
		return <Loading isLoading />;
	}
	const settlementEnabled = currentAccount.company?.settlementEnabled ?? false;
	const pageTitle = settlementEnabled
		? "入居者への請求書・精算書作成"
		: "入居者への請求書作成";
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: pageTitle }
	] as const satisfies ComponentProps<typeof ClientDealLayout>["items"];
	const handleClickPreview = (): void => {
		const path = getDownloadPath(id, "deal_billing_rentee");
		window.open(path);
	};

	const handleClickSettlementPreview = (): void => {
		const path = getSettlementDownloadPath(String(id), "rentee");
		window.open(path);
	};
	const handleMutateConstructions = async (): Promise<void> => {
		await mutateBillings();
	};
	const handleCreate = async (
		submitValue: DealConstructionEdit | DealSettlementEdit
	): Promise<NskResponse> => postDealBilling(submitValue, deal.id, "rentee");
	const handleUpdate = async (
		submitValue: DealConstructionEdit | DealSettlementEdit,
		billingId: number | null
	): Promise<NskResponse> => {
		if (billingId === null) {
			throw new Error("billingId cannot be null");
		}
		return updateDealBilling(submitValue, billingId, deal.id);
	};
	const handleDestroy = async (
		deleteBilling: DealConstruction | DealSettlement
	): Promise<NskResponse> => {
		if (deleteBilling.type !== "DealBilling") {
			throw new Error(`Illegal construction type: ${deleteBilling.type}`);
		}
		if (deleteBilling.billingType !== "rentee") {
			throw new Error(`Illegal billing type: ${deleteBilling.billingType}`);
		}
		return destroyBilling(deleteBilling, deal.id);
	};
	const handleUpdateSeq = async (
		newBillings: DealConstruction[] | DealSettlement[]
	): Promise<void> => {
		await updateSeq(newBillings, deal.id);
	};
	const handleClickGenerateBillings = async (): Promise<void> => {
		const result = await generateBillings(deal.id, "rentee");
		if (isSuccess(result)) {
			await mutateBillings();
			notify({ content: "請求を再作成しました。", variant: "success" });
			handleClose();
			return;
		}
		notify({ content: "エラーが発生しました。", variant: "error" });
		handleClose();
	};

	return (
		<ItandiAccountContext.Provider value={currentAccount}>
			<DefaultV2>
				<ClientDealLayout deal={deal} items={items}>
					<Flex justifyContent="space-between">
						<h2 className={Styles.EstimateTitle}>{pageTitle}</h2>
						{!isSp ? (
							<Flex gap={designTokens["spacing/xs"]}>
								{settlementEnabled ? (
									<Button
										disabled={billingContext.dealSettlements.length === 0}
										onClick={handleClickSettlementPreview}
										variant="secondary"
									>
										精算書ダウンロード
									</Button>
								) : null}
								<Button
									disabled={billingContext.dealConstructions.length === 0}
									onClick={handleClickPreview}
									variant="secondary"
								>
									請求書ダウンロード
								</Button>
							</Flex>
						) : null}
					</Flex>
					<ConstructionTop
						billingTarget="rentee"
						constructionContext={billingContext}
						handleClickChangeAmount={(): void => {
							setShowChangeAmountModal(true);
						}}
						handleClickGenerateBillings={handleOpen}
						readonly={false}
					/>
					{showChangeAmountModal ? (
						<ChangeAmountModal
							billingContext={billingContext}
							billingType="rentee"
							onClose={(): void => {
								setShowChangeAmountModal(false);
							}}
						/>
					) : null}
					{isOpen ? (
						<ConfirmModal
							confirmType="save"
							handleAccept={handleClickGenerateBillings}
							handleCancel={handleClose}
							isOpen={isOpen}
						>
							工事見積りから請求を再作成しますか？
							<Alert marginTop={8}>
								現在登録されている入居者への請求情報は削除されます
							</Alert>
						</ConfirmModal>
					) : null}
					<div className={Styles.Container}>
						<div className={Styles.EstimateAddForm}>
							<InvoiceItemAddForm
								billingType="rentee"
								handleCreate={handleCreate}
								mutateConstructions={handleMutateConstructions}
							/>
						</div>
						<div className={Styles.EstimateList}>
							{billingContext.dealSettlements.length > 0 && (
								<SettlementList
									constructionContext={billingContext}
									handleDestroy={handleDestroy}
									handleMutateSettlements={handleMutateConstructions}
									handleUpdate={handleUpdate}
									handleUpdateSeq={handleUpdateSeq}
									target="rentee"
								/>
							)}
							<ConstructionList
								companyType="client"
								constructionContext={billingContext}
								deal={deal}
								dealConstructionItems={dealConstructionItems}
								docType="billing"
								handleDestroy={handleDestroy}
								handleMutateConstructions={handleMutateConstructions}
								handleUpdate={handleUpdate}
								handleUpdateSeq={handleUpdateSeq}
								readonly={false}
								target="rentee"
							/>
						</div>
					</div>
				</ClientDealLayout>
			</DefaultV2>
		</ItandiAccountContext.Provider>
	);
}
